import { diagnosticSentence, plainTime } from "./diagnosticWording";
import { themeVar as colors } from "../design-tokens";
import { measure } from "../ui/measures";
import {
  COULD_NOT_ASK_BODY, COULD_NOT_ASK_BODY_NO_RETRY, COULD_NOT_ASK_TITLE, NOT_REAL_DATA, canRetry,
  compGapGroups, couldNotAsk, couldNotAskReasons, foundLine, gapLabel, noSalesLine,
  noCompsCopy, notConnectedLine, subjectGaps,
  type CascadeGap, type CascadeMeta, type CascadeNotice, type ProviderDiagnostic,
} from "./cascade";

/**
 * THE FOUR THINGS THE SOURCE CASCADE PUTS ON SCREEN — and the three states that are easy to render
 * wrong. See `cascade.ts` for the rules; this file is only the pixels.
 *
 * Everything here is deliberately tiny and deliberately dumb: no fetching, no state, no decisions.
 * The decisions live in `cascade.ts` as pure functions so they can be tested without a DOM, and so
 * "an outage is not a no-match" is enforced in one place instead of at four render sites.
 *
 * THE THREE STATES, AND WHY EACH LOOKS THE WAY IT DOES:
 *
 *   NOT REAL DATA (`NotRealDataNotice`) — loud. Filled, bordered, `role="alert"`, first on the
 *   page. `env` is `sandbox` in production right now, so this is not a hypothetical: sandbox
 *   records are complete and plausible and may describe a different house entirely. An agent who
 *   mistakes one for a finding puts it in front of a client. It is the only thing in this file
 *   allowed to shout.
 *
 *   WE COULD NOT ASK (`CouldNotAskPanel`) — different words, visibly different state, and the retry
 *   is the panel's own primary button rather than something to go and find. It never says "no
 *   comparable sales found", because that sentence sends an agent hunting for a property that was
 *   always there.
 *
 *   A GAP (`GapQuestions`) — quiet. Neutral border, secondary text, no alert role, no red. A
 *   missing bath split is a question the record cannot answer and the agent can, and it renders as
 *   the labelled blank it is. Spec §4: "A gap is a question, not an error."
 *
 * And the fourth thing is one plain sentence (`FoundLine`) — never a provenance table.
 */

// ---------------------------------------------------------------------------------------------
// 1. Notices
// ---------------------------------------------------------------------------------------------

/**
 * The notice kinds that are TRUE, must keep travelling, and are not for the person on the screen.
 *
 * `not_deliverable` is `cotality.NOT_DELIVERABLE_NOTE`. Its own first two words are "Internal
 * review only", and it names an environment variable —
 * `VALUBRIEF_COTALITY_DELIVERABLE_LICENSE=0` — in a banner an agent reads while working. It is a
 * message from this codebase to whoever is operating it, and it was being painted at the customer.
 * Naming an internal flag on a customer surface is also the kind of detail that should not leave
 * the building.
 *
 * THIS IS A RENDERING DECISION AND NOTHING ELSE. Every part of the gate is deliberately left
 * alone: `VALUBRIEF_COTALITY_DELIVERABLE_LICENSE` still defaults to 0 and is NOT flipped;
 * `cotality.py` still stamps `_note` on the payload at :574; `source_cascade._gate_notices` still
 * emits the notice; `noticesFromGates` still returns it, and its tests still assert that it does.
 * The compliance stamp is intact end to end — it simply is not painted. If counsel resolves the
 * licence the stamp stops being emitted at the source, which is where that decision belongs.
 *
 * Removed markup is archived, never deleted:
 * `.backups/CascadePanels.tsx.20260816T053816Z.bak` holds the version that painted it.
 */
const INTERNAL_ONLY_KINDS: ReadonlySet<string> = new Set(["not_deliverable"]);

/**
 * `notices[]`, rendered by kind. `not_real_data` is prominent; everything else is a quieter
 * standing statement. Kinds in `INTERNAL_ONLY_KINDS` are dropped here and nowhere else.
 *
 * The TEXT IS THE SERVER'S, always. This component chooses weight and colour and whether a given
 * kind belongs on a customer's screen — it words nothing, because the sandbox and licence
 * wordings are compliance surface, not copy.
 */
export function CascadeNotices({ notices }: { notices: CascadeNotice[] | null | undefined }) {
  const list = (notices ?? []).filter((n) => !INTERNAL_ONLY_KINDS.has(n.kind));
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      {list.map((notice, i) =>
        notice.kind === NOT_REAL_DATA ? (
          <NotRealDataNotice key={`${notice.kind}-${i}`} text={notice.text} />
        ) : (
          <StandingNotice key={`${notice.kind}-${i}`} text={notice.text} />
        )
      )}
    </div>
  );
}

function NotRealDataNotice({ text }: { text: string }) {
  return (
    <div
      role="alert"
      data-testid="cascade-not-real-data"
      style={{
        padding: "14px 16px",
        borderRadius: 10,
        background: colors.cautionBg,
        border: `1px solid ${colors.cautionLine}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: colors.caution }}>
        These figures are not real property data
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.textPrimary, maxWidth: measure.copy }}>
        {text}
      </p>
    </div>
  );
}

function StandingNotice({ text }: { text: string }) {
  return (
    <p
      role="status"
      style={{
        margin: 0,
        padding: "10px 12px",
        borderRadius: 8,
        background: colors.amberBg,
        border: `1px solid ${colors.amberLine}`,
        fontSize: 13,
        lineHeight: 1.55,
        color: colors.textPrimary,
        maxWidth: measure.copy + 26,
      }}
    >
      {text}
    </p>
  );
}

// ---------------------------------------------------------------------------------------------
// 2. "We could not ask"
// ---------------------------------------------------------------------------------------------

/**
 * The outage state. Rendered whenever `blocking` is non-empty, on every screen that would
 * otherwise be describing what was found.
 *
 * THE RETRY IS THE POINT. A source that could not be reached is very often reachable a moment
 * later, and the alternative the agent otherwise falls back on — re-uploading a file, or deciding
 * the property has no comparable sales — is both expensive and wrong. So the primary action here is
 * "Try again", it is the first control in the panel, and it is never disabled except while it is
 * running.
 */
export function CouldNotAskPanel({
  meta,
  onRetry,
  retrying,
  onAddFile,
}: {
  meta: Pick<CascadeMeta, "blocking"> | null | undefined;
  onRetry?: () => void;
  retrying?: boolean;
  /** The action that works when a retry cannot (2026-10-18): the agent's own comps file. */
  onAddFile?: () => void;
}) {
  const blocked = couldNotAsk(meta);
  if (!blocked.length) return null;
  const reasons = couldNotAskReasons(meta);
  // NO DEAD RETRY (2026-10-18). When every blocked source is held offline, backing off, switched
  // off or out of allowance, "Try again" would get the same answer; it is replaced by the action
  // that does work, and the body says why in the place the retry was.
  const retry = canRetry(meta);
  return (
    <div
      role="alert"
      data-testid="cascade-could-not-ask"
      style={{
        marginTop: 14,
        padding: "14px 16px",
        borderRadius: 10,
        background: colors.amberBg,
        border: `1px solid ${colors.amberLine}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: colors.amber }}>
        {COULD_NOT_ASK_TITLE}
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.textPrimary, maxWidth: measure.copy }}>
        {retry ? COULD_NOT_ASK_BODY : COULD_NOT_ASK_BODY_NO_RETRY}
      </p>
      {reasons.length > 0 && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: colors.textSecondary, maxWidth: measure.copy }}>
          {reasons.map((reason, i) => (
            <li key={i}>{reason}</li>
          ))}
        </ul>
      )}
      {!retry && onAddFile && (
        <button className="vb-btn"
          type="button"
          onClick={onAddFile}
          style={{
            marginTop: 12, padding: "9px 18px", borderRadius: 8, border: "none",
            background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          Add your own comps file
        </button>
      )}
      {retry && onRetry && (
        <button className="vb-btn"
          type="button"
          onClick={onRetry}
          disabled={retrying}
          aria-disabled={retrying}
          style={{
            marginTop: 12,
            padding: "9px 18px",
            borderRadius: 8,
            border: "none",
            background: retrying ? colors.neutralChip : colors.accent,
            color: retrying ? colors.textSecondary : "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: retrying ? "not-allowed" : "pointer",
          }}
        >
          {retrying ? "Trying again…" : "Try again"}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// 2b. Cotality is offline AND the county record cannot help
// ---------------------------------------------------------------------------------------------

/**
 * `Cotality is offline. CAD data is needed to proceed.` — and nothing else, ever.
 *
 * WHEN THIS RENDERS, AND WHY THE CONDITION IS SO NARROW. `cad_required` is set by the server ONLY
 * when MLS, Cotality and the county CAD record have all failed to answer for THIS address and no
 * upload was supplied. Cotality being offline is not on its own enough: when the county record
 * answers, the fall-through worked, the agent has what they need, and a notice about a provider
 * they never asked for is noise. A fall-through that works is not an event (spec §5.1).
 *
 * WHERE IT RENDERS. Wherever the control the agent pressed lives — this component is mounted next
 * to that control, not at the top of the page and not in a toast. The defect it closes is a dead
 * control: on 2026-09-22 a 502 from the provider left an empty comp grid and no explanation, and
 * on the legacy page it rendered the words "No property matched that address", blaming the
 * agent's typing for our outage.
 *
 * THE TEXT IS THE SERVER'S. Both lines are `source_cascade` / `cotality` literals, owner-set. This
 * component chooses weight and placement and words nothing — the same rule every other panel here
 * follows.
 */
export function CadRequiredPanel({
  meta,
  onProvide,
  provideLabel = "Upload a CAD sheet",
}: {
  meta: Pick<CascadeMeta, "cad_required" | "cad_notice"> | null | undefined;
  /** The control that acts on the advice. Omitted where the caller has no such control — in which
   * case the line still says how, in words, and nothing pretends to be clickable. */
  onProvide?: () => void;
  provideLabel?: string;
}) {
  // `undefined` is not `false` and is certainly not `true`: an older server omits the field, and
  // painting a blocking notice on "no claim either way" would be the first dishonest thing on the
  // screen. Only an explicit `true` renders.
  if (meta?.cad_required !== true) return null;
  const notice = meta?.cad_notice ?? null;
  if (!notice) return null;
  return (
    <div
      role="alert"
      data-testid="cascade-cad-required"
      style={{
        marginTop: 14,
        padding: "14px 16px",
        borderRadius: 10,
        background: colors.amberBg,
        border: `1px solid ${colors.amberLine}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: colors.amber }}>{notice.text}</p>
      {notice.how && (
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13,
            lineHeight: 1.55,
            color: colors.textPrimary,
            maxWidth: measure.copy,
          }}
        >
          {notice.how}
        </p>
      )}
      {onProvide && (
        <button
          className="vb-btn"
          type="button"
          onClick={onProvide}
          style={{
            marginTop: 12,
            padding: "9px 18px",
            borderRadius: 8,
            border: "none",
            background: colors.accent,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {provideLabel}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// 3. The one plain line
// ---------------------------------------------------------------------------------------------

/**
 * "We found 8 nearby sales from public records. Check the ones you want to use."
 *
 * One sentence, not a provenance table. It renders nothing at all during an outage — `foundLine`
 * returns null there and `noSalesLine` refuses too, so this component structurally cannot print an
 * empty-result message over a source that was never asked. The `CouldNotAskPanel` above says the
 * true thing instead.
 */
export function FoundLine({
  meta,
  compCount,
}: {
  meta: Pick<CascadeMeta, "answered_by" | "blocking"> & { comp_ladder?: CascadeMeta["comp_ladder"] } | null | undefined;
  compCount: number;
}) {
  const found = foundLine(meta, compCount);
  const none = found ? null : (noSalesLine(meta, compCount) ?? notConnectedLine(meta, compCount));
  const line = found ?? none;
  if (!line) return null;
  return (
    <p
      data-testid="cascade-found-line"
      style={{
        marginTop: 10,
        fontSize: 14,
        lineHeight: 1.55,
        color: found ? colors.textPrimary : colors.textSecondary,
        maxWidth: measure.copy,
      }}
    >
      {line}
    </p>
  );
}

// ---------------------------------------------------------------------------------------------
// 4. Gaps, as questions
// ---------------------------------------------------------------------------------------------

/**
 * What is still missing, addressed to the person who can supply it — in neutral type, with no alert
 * role and no caution colour anywhere in it.
 *
 * THIS IS AN INDEX, NOT A SECOND FORM. Every field named here already has its own labelled, empty
 * input in the grid on the same screen; duplicating the control would mean two inputs over one
 * value. What the list adds is the part the grid cannot say: WHY the blank is there — "public
 * records for comparable sales carry no parking field at all" — which is the difference between a
 * form that looks unfinished and a form that has told you what it needs.
 *
 * `scope` splits it in two because the two have different answers. A subject gap is one field on
 * this home. A comp gap is the same field across several sales, and forty copies of one sentence is
 * not a list an agent can act on — so they are rolled up by field with the sales named.
 */
export function GapQuestions({
  gaps,
  scope = "all",
  title = "A few things only you can fill in",
}: {
  gaps: CascadeGap[] | null | undefined;
  /** Which half to render. The two belong on different steps — a subject gap is one field on this
   * home and sits with the property detail; a comp gap is a column of the sales table and sits
   * with it. Splitting them here rather than rendering one long list on both screens is the same
   * "ask for a fact once, where it is answered" rule the wizard already follows. */
  scope?: "subject" | "comp" | "all";
  title?: string;
}) {
  const subject = scope === "comp" ? [] : subjectGaps(gaps);
  const comps = scope === "subject" ? [] : compGapGroups(gaps);
  if (!subject.length && !comps.length) return null;
  return (
    <section
      data-testid="cascade-gaps"
      aria-label={title}
      style={{
        marginTop: 18,
        padding: 16,
        borderRadius: 10,
        background: colors.bgSurface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>{title}</h2>
      <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.textSecondary, maxWidth: measure.copy }}>
        No record states these, so nothing has been filled in for them. They are blank on the form
        above, waiting — not mistakes.
      </p>
      <dl style={{ margin: "12px 0 0" }}>
        {subject.map((gap) => (
          <GapRow key={`subject-${gap.field}`} label={gapLabel(gap)} why={gap.why} />
        ))}
        {comps.map((group) => (
          <GapRow
            key={`comp-${group.field}`}
            label={group.label}
            why={group.why}
            on={group.comps}
          />
        ))}
      </dl>
    </section>
  );
}

/** One question. The value column is an em dash — the same "unknown is a dash, never a zero" rule
 * the grid itself follows, said in the place that is explaining the dash. */
function GapRow({ label, why, on }: { label: string; why: string; on?: string[] }) {
  return (
    <div style={{ marginTop: 10, display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
      <dt style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, minWidth: 130 }}>
        {label}
      </dt>
      <dd style={{ margin: 0, flex: "1 1 320px", maxWidth: measure.copy }}>
        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, color: colors.textSecondary }}>—</span>
        <span style={{ display: "block", marginTop: 2, fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
          {why}
          {on && on.length > 0 && ` On: ${on.join(", ")}.`}
        </span>
      </dd>
    </div>
  );
}

/**
 * THE FILE WE DID NOT READ, SAID WHERE THE AGENT ATTACHED IT (2026-09-22, owner direction).
 *
 * The rule, as the owner set it: when the free sources above (MLS -> Cotality -> county record)
 * filled every field the report needs, an attached file is NOT read — reading it is a model call
 * that would add nothing (`source_cascade.file_parse_decision`, spec §2.1) — unless the agent
 * chooses to use their own data. This panel is that choice.
 *
 * NOT A DEAD CONTROL AND NOT A SILENT NO-OP. A skipped file that nobody mentioned would be a file
 * that vanished; the line names it and says why. The button acts every time it renders: the caller
 * only mounts this while it still holds the files in memory, so the override can always run.
 *
 * WHAT THE OVERRIDE DOES, STATED HONESTLY: the file's comparables replace the public-record ones
 * (`applyExtract` sets the comp table from the file), and its subject figures fill every field the
 * agent has not confirmed. A value the agent ticked is never overwritten — `mergeGrid`'s rule.
 *
 * Calm, sentence case, `role="status"`: nothing is wrong. The report can be built as it stands.
 */
export function UploadSkippedPanel({
  fileNames,
  onUseFile,
  busy = false,
}: {
  fileNames: string[];
  onUseFile: () => void;
  busy?: boolean;
}) {
  if (!fileNames.length) return null;
  const named = fileNames.length === 1 ? fileNames[0] : `${fileNames.length} files`;
  return (
    <div
      role="status"
      data-testid="cascade-upload-skipped"
      style={{
        marginTop: 14,
        padding: "12px 14px",
        borderRadius: 10,
        background: colors.bgSurface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: colors.textPrimary, maxWidth: measure.copy }}>
        We didn&rsquo;t read {named}. The public record filled every field this report needs, so
        reading it would have added nothing.
      </p>
      <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.textSecondary, maxWidth: measure.copy }}>
        Using your file replaces these comparables with yours and fills any subject figure you
        haven&rsquo;t confirmed.
      </p>
      <button
        className="vb-btn"
        type="button"
        onClick={onUseFile}
        disabled={busy}
        style={{
          marginTop: 10,
          padding: "8px 16px",
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          background: "transparent",
          color: colors.textPrimary,
          fontSize: 13,
          fontWeight: 600,
          cursor: busy ? "default" : "pointer",
        }}
      >
        {busy ? "Reading your file…" : "Use my file instead"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// No comparable sales, for a regular user (owner, 2026-10-19; copy set 2026-10-20, item 1F)
// ---------------------------------------------------------------------------------------------

/**
 * THE CALM CARD. A regular user whose search found no sales is told what to do next, not which
 * vendor did what: no provider name, no reason code, no "try again". It is a step card (the same
 * surface, border and radius as the subject and comps cards), not an alert — the agent can go on.
 * The sentence is one of the owner's two, verbatim (`noCompsCopy`).
 *
 * Every button is a real control (no dead controls):
 *   * "Upload comp file" opens the flow's own file input.
 *   * "Request MLS access" goes to the existing request flow — the MLS access screen, whose form
 *     posts `/api/mls-access/request`. Shown only to someone not connected, and only where that
 *     screen is switched on (`mlsOffered`), because elsewhere the link would land on nothing.
 *   * "Adjust search" renders ONLY when `onAdjustSearch` is passed. That is the hook for the comp
 *     search-criteria control (fan-out item 1G, `slice/comp-search-map`), which does not exist on
 *     this branch's base; wire it there with `onAdjustSearch={() => openCompSearch()}`.
 */
export function NoCompsCard({
  mlsConnected,
  mlsOffered,
  onUpload,
  onAdjustSearch,
}: {
  mlsConnected: boolean;
  mlsOffered: boolean;
  onUpload: () => void;
  /** HOOK FOR 1G: opens the comp search-criteria control. Omit until that control exists. */
  onAdjustSearch?: () => void;
}) {
  const offerMls = !mlsConnected && mlsOffered;
  const btn = {
    padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
    textDecoration: "none", display: "inline-block", lineHeight: 1.4,
  } as const;
  const secondary = {
    ...btn, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.accent,
  } as const;
  return (
    <section
      data-testid="no-comps-card"
      aria-label="Comparable sales"
      style={{
        marginTop: 14, padding: "16px 18px", borderRadius: 12,
        background: colors.bgSurface, border: `1px solid ${colors.border}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: colors.textPrimary, maxWidth: measure.copy }}>
        {noCompsCopy(mlsConnected)}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
        {onAdjustSearch && (
          <button type="button" className="vb-btn" data-testid="no-comps-adjust-search"
                  onClick={onAdjustSearch} style={secondary}>
            Adjust search
          </button>
        )}
        <button type="button" className="vb-btn" onClick={onUpload}
                style={{ ...btn, border: "none", background: colors.accent, color: "#fff" }}>
          Upload comp file
        </button>
        {offerMls && (
          <a href="/mls-access" className="vb-btn" style={secondary}>
            Request MLS access
          </a>
        )}
      </div>
    </section>
  );
}

/**
 * SOURCE DIAGNOSTICS, FOR THE ALLOW-LIST ONLY (owner, 2026-10-19; approved 2026-10-20). The server
 * sends `provider_diagnostics` only to a designated admin whose e-mail is in
 * `VALUBRIEF_PROVIDER_DIAG_EMAILS`, and `/api/admin/source-diagnostics` answers 403 to everyone
 * else, so this renders nothing for anyone else by construction. Compact and calm: a small table in the card's own
 * tokens, no alarm color — an outage here is information for the operator, not an error the
 * agent made.
 */
/** A machine token (`not_available`) may break after an underscore, never mid-word. */
const breakable = (token: string) => token.replace(/_/g, "_\u200b");

export function SourceDiagnosticsPanel({ rows }: { rows: ProviderDiagnostic[] | null | undefined }) {
  if (!rows || !rows.length) return null;
  // The per-resolve rows carry a scope (subject / comps); the admin route's per-provider rows do
  // not, and an empty column is noise.
  const withScope = rows.some((r) => !!r.scope);
  // `whiteSpace` and `overflowWrap` are inline on purpose: the app-wide `.vb-ui thead th` rule is
  // `nowrap` inside a fixed-layout table, which at 390 px laid one heading over the next (measured
  // in Chromium, 2026-10-20). A heading wraps; a long machine token breaks rather than overlaps.
  const cell = {
    padding: "4px 10px 4px 0", fontSize: 12, lineHeight: 1.5, textAlign: "left" as const,
    whiteSpace: "normal" as const, overflowWrap: "break-word" as const, verticalAlign: "top" as const,
  };
  return (
    <section
      data-testid="source-diagnostics"
      aria-label="Source diagnostics"
      style={{
        marginTop: 14, padding: "12px 16px", borderRadius: 12,
        background: colors.bgSurface, border: `1px solid ${colors.border}`,
      }}
    >
      <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>
        Source diagnostics
      </p>
      {/* ONE PLAIN SENTENCE PER ROW. The wording is `diagnosticWording.ts`; the raw tokens and the
          ISO time are not removed, they sit in the fold below. */}
      <ul data-testid="source-diagnostics-sentences"
          style={{ margin: 0, padding: 0, listStyle: "none", color: colors.textPrimary }}>
        {rows.map((r, i) => {
          const when = plainTime(r.last_answer_at);
          return (
            <li key={i} data-diag-rung={r.rung}
                style={{ fontSize: 13, lineHeight: 1.5, padding: "3px 0", overflowWrap: "break-word" }}>
              {r.scope && (
                <span style={{ color: colors.textSecondary }}>
                  {r.scope === "subject" ? "Subject: " : "Comparable sales: "}
                </span>
              )}
              {diagnosticSentence(r)}
              {when && <span style={{ color: colors.textSecondary }}> Last answered {when}.</span>}
            </li>
          );
        })}
      </ul>
      <details data-testid="source-diagnostics-details" style={{ marginTop: 8 }}>
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>
          Show details ({rows.length})
        </summary>
        <div style={{ marginTop: 6 }}>
          <table className="vb-diag-table" style={{ borderCollapse: "collapse", color: colors.textPrimary, width: "100%" }}>
            <thead>
              <tr style={{ color: colors.textSecondary }}>
                <th style={cell}>Rung</th>{withScope && <th style={cell}>Scope</th>}<th style={cell}>Status</th>
                <th style={cell}>Reason code</th><th style={cell}>Last answer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-diag-rung={r.rung}>
                  <td style={cell} data-label="Rung">{r.label || r.rung}</td>
                  {withScope && <td style={cell} data-label="Scope">{r.scope}</td>}
                  <td style={cell} data-label="Status">{breakable(r.status)}</td>
                  <td style={cell} data-label="Reason code">{r.reason_code ? breakable(r.reason_code) : "\u2014"}</td>
                  <td style={cell} data-label="Last answer">{r.last_answer_at ? r.last_answer_at.replace("T", " ").replace("Z", " UTC") : "None recorded"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
